import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    Output
} from "@angular/core";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { LgPromptDialog } from "../../lg-prompt-dialog/lg-prompt-dialog.component";

@Component({
    standalone: false,
    selector: "lg-editable-grid-text",
    templateUrl: "./lg-editable-grid-text.component.html",
    styleUrl: "./lg-editable-grid-text.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LgEditableGridTextComponent {
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _promptDialog = inject(LgPromptDialog);
    private _lgTranslate = inject(LgTranslateService);

    protected _isEditing = false;

    @Input("model") _value = "";
    protected _valueOriginal = "";

    @Output("modelChange") protected readonly _valueChange = new EventEmitter<string>();

    @Input("disabled") _disabled = false;
    @Input("confirmMessageDisabled") _confirmMessageDisabled = false;
    @Input("nullOrEmptyValueDisabled") _nullOrEmptyValueDisabled = false;

    @Input("markFocusOn") protected _markFocusOn = ".table__row";

    /**
     * Tooltip shown on the edit icon. No tooltip when omitted.
     */
    @Input() editTooltip?: string | null;

    /**
     * Specifies the placeholder in input.
     *
     * @param placeholder default = undefined
     */
    @Input() placeholder?: string | boolean | null | undefined;

    @Output("lgFocus") protected readonly _focusCb = new EventEmitter<void>();
    @Output("lgBlur") protected readonly _blurCb = new EventEmitter<string>();
    @Output("lgSave") protected readonly _saveCb = new EventEmitter<string>();

    startEditing(): void {
        this._isEditing = true;
        this._valueOriginal = this._value;
        this._changeDetectorRef.markForCheck();
    }

    protected async _onFocus(): Promise<void> {
        this._focusCb.emit();
    }

    protected async _onBlur(): Promise<void> {
        if (!this._confirmMessageDisabled && this._value !== this._valueOriginal) {
            const response = await this._promptDialog.confirm(
                this._lgTranslate.translate("FW._LgEditableGridText.ConfirmSave.Title"),
                this._lgTranslate.translate("FW._LgEditableGridText.ConfirmSave.Body"),
                {
                    buttons: [
                        {
                            id: "save",
                            name: this._lgTranslate.translate(
                                "FW._LgEditableGridText.ConfirmSave.Save"
                            ),
                            isConfirmAction: true
                        },
                        {
                            id: "cancel",
                            name: this._lgTranslate.translate(
                                "FW._LgEditableGridText.ConfirmSave.Cancel"
                            ),
                            isCancelAction: true
                        }
                    ]
                }
            );

            switch (response) {
                case "save":
                    this._applyValue(this._value);
                    break;
                case "cancel":
                    this._value = this._valueOriginal;
                    this._changeDetectorRef.markForCheck();
                    break;
            }
        } else {
            this._applyValue(this._value);
        }

        this._blurCb.emit(this._value);
        this._isEditing = false;
    }

    protected _onPressEnter(): void {
        if (this._value === this._valueOriginal) return;
        this._applyValue(this._value);
    }

    protected async _startEditing($event: MouseEvent): Promise<void> {
        $event.stopPropagation();
        this.startEditing();
    }

    private _applyValue(value: string): void {
        if (this._nullOrEmptyValueDisabled && !value) {
            this._value = this._valueOriginal;
            this._changeDetectorRef.markForCheck();
            return;
        }

        this._valueOriginal = value;
        this._saveCb.next(value);
        this._valueChange.emit(value);
        this._changeDetectorRef.markForCheck();
    }
}
